import { useState, useCallback, useEffect } from 'react';
import { useCapabilities } from '../../../../Context/CapabilityContext';

const useCaseData = (threadId, authenticatedAxios) => {
  /**
   * The immigration program catalog (/expert-programs/active, /ircc/application-types) is
   * now gated server-side. Without this guard the hook fetched it on mount for EVERY
   * adviser, so a non-immigration adviser would open a case and immediately collect 403s.
   * The capability profile resolves a beat after mount, hence the dedicated effect below.
   */
  const { features } = useCapabilities();
  const canUsePrograms = features.immigrationPrograms === true;
  // Data states
  const [analysisData, setAnalysisData] = useState(null);
  const [files, setFiles] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState({ analysis: false, files: false, documents: false });
  
  // IRCC Program states
  const [applicationTypes, setApplicationTypes] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [programChecklist, setProgramChecklist] = useState([]);
  const [userFormSessions, setUserFormSessions] = useState([]);
  const [loadingProgram, setLoadingProgram] = useState(false);
  
  // Pipeline name for display in Progress tab
  const [pipelineName, setPipelineName] = useState('');

  // ============================================
  // DATA LOADING FUNCTIONS (reusable for refresh)
  // ============================================

  const loadProgress = useCallback(async () => {
    if (!threadId) return;
    try {
      const response = await authenticatedAxios.get(`/case-progress/${threadId}`);
      if (response.data?.template) {
        // Set the pipeline name from the template
        if (response.data.template.name) {
          setPipelineName(response.data.template.name);
        }
        // Return the template so callers can use it
        return response.data;
      }
      // Also check if there's a pipeline name in the response
      if (response.data?.pipelineName) {
        setPipelineName(response.data.pipelineName);
      }
      return response.data;
    } catch (error) {
      console.log('No saved progress, using defaults');
      return null;
    }
  }, [threadId, authenticatedAxios]);

  const loadAnalysis = useCallback(async () => {
    if (!threadId) return;
    setLoading(prev => ({ ...prev, analysis: true }));
    try {
      const response = await authenticatedAxios.get(`/analysis/case-options/${threadId}`);
      if (response.data?.analysis) {
        setAnalysisData(response.data.analysis);
      }
    } catch (error) {
      console.log('Error loading analysis:', error);
    } finally {
      setLoading(prev => ({ ...prev, analysis: false }));
    }
  }, [threadId, authenticatedAxios]);

  const loadFiles = useCallback(async () => {
    if (!threadId) return;
    setLoading(prev => ({ ...prev, files: true }));
    try {
      const response = await authenticatedAxios.get(`/conversation-files/${threadId}`);
      const fileList = response.data?.files || response.data || [];
      setFiles(Array.isArray(fileList) ? fileList : []);
    } catch (error) {
      console.log('Error loading files:', error);
      setFiles([]);
    } finally {
      setLoading(prev => ({ ...prev, files: false }));
    }
  }, [threadId, authenticatedAxios]);

  const loadDocuments = useCallback(async () => {
    if (!threadId) return;
    setLoading(prev => ({ ...prev, documents: true }));
    try {
      const response = await authenticatedAxios.get(`/user-documents/thread/${threadId}`);
      const docs = response.data?.documents || response.data;
      setDocuments(Array.isArray(docs) ? docs : []);
    } catch (error) {
      console.log('Error loading documents:', error);
      setDocuments([]);
    } finally {
      setLoading(prev => ({ ...prev, documents: false }));
    }
  }, [threadId, authenticatedAxios]);

  // Load saved selected program for this conversation
  const loadSelectedProgram = useCallback(async () => {
    if (!threadId) return;
    try {
      const response = await authenticatedAxios.get(`/conversations/${threadId}/program`);
      if (response.data.success && response.data.selectedProgramId) {
        // We need to wait for applicationTypes to be loaded first
        // The actual setting will happen in loadApplicationTypes
        return response.data.selectedProgramId;
      }
    } catch (error) {
      console.log('No saved program selection found');
    }
    return null;
  }, [threadId, authenticatedAxios]);

  // Load user form sessions for this case
  const loadUserFormSessions = useCallback(async () => {
    if (!threadId) return;
    try {
      const response = await authenticatedAxios.get('/form-filling/user-sessions', {
        params: { conversationId: threadId }
      });
      if (response.data.success) {
        setUserFormSessions(response.data.sessions || []);
      }
    } catch (error) {
      console.log('Error loading form sessions:', error);
    }
  }, [threadId, authenticatedAxios]);

  // Load checklist for selected program
  const loadProgramChecklist = useCallback(async (typeId) => {
    if (!typeId) return;
    setLoadingProgram(true);
    try {
      const completedForms = userFormSessions
        .filter(s => s.completionPercent >= 100)
        .map(s => s.formId || s.formName);
      
      const uploadedDocs = files.map(f => f.docId || f.fileName);
      
      const response = await authenticatedAxios.post('/ircc/checklist', {
        typeId,
        completedForms,
        uploadedDocs
      });
      
      let checklist = response.data.success ? response.data.checklist : [];

      // Merge explicit requirement-fulfillment status (attached files) on top of
      // the heuristic checklist so linked documents show as complete everywhere.
      try {
        const reqRes = await authenticatedAxios.get(`/requirements/${threadId}`, { params: { programTypeId: typeId } });
        const map = {};
        for (const r of [...(reqRes.data.forms || []), ...(reqRes.data.documents || [])]) {
          map[r.requirementKey] = r;
        }
        checklist = checklist.map(item => {
          const r = map[item.id];
          if (!r) return item;
          const fulfilled = r.status === 'fulfilled' || r.status === 'verified';
          return { ...item, reqStatus: r.status, reqFiles: r.files || [], reqFulfilledBy: r.fulfilledBy, completed: item.completed || fulfilled };
        });
      } catch (e) {
        console.warn('[CaseFile] Could not merge requirement status:', e?.message);
      }

      setProgramChecklist(checklist);
    } catch (error) {
      console.log('Error loading checklist:', error);
    } finally {
      setLoadingProgram(false);
    }
  }, [userFormSessions, files, authenticatedAxios, threadId]);

  // --- Requirement fulfillment actions (Phase 2) ---
  const linkRequirement = useCallback(async (typeId, item, file) => {
    await authenticatedAxios.post(`/requirements/${threadId}/link`, {
      programTypeId: typeId,
      requirementKey: item.id,
      kind: item.category === 'form' ? 'form' : 'document',
      name: item.name,
      required: item.required,
      file: { fileId: file.id || file.fileId, fileName: file.originalName || file.fileName || file.name, fileUrl: file.fileUrl, storageKey: file.fileName }
    });
  }, [authenticatedAxios, threadId]);

  const unlinkRequirement = useCallback(async (typeId, item) => {
    await authenticatedAxios.post(`/requirements/${threadId}/unlink`, {
      programTypeId: typeId, requirementKey: item.id
    });
  }, [authenticatedAxios, threadId]);

  const verifyRequirement = useCallback(async (typeId, item) => {
    await authenticatedAxios.post(`/requirements/${threadId}/verify`, {
      programTypeId: typeId, requirementKey: item.id
    });
  }, [authenticatedAxios, threadId]);

  const sendDocumentRequest = useCallback(async (typeId, items, message) => {
    await authenticatedAxios.post(`/requirements/${threadId}/request`, {
      programTypeId: typeId,
      message: message || '',
      items: items.map(it => ({ requirementKey: it.id, name: it.name, kind: it.category === 'form' ? 'form' : 'document' }))
    });
  }, [authenticatedAxios, threadId]);

  // Load IRCC application types and restore saved selection
  // Uses expert-programs/active to only show programs the expert has activated for their firm
  const loadApplicationTypes = useCallback(async () => {
    if (!canUsePrograms) return;   // not this discipline's toolset
    try {
      // First try to get only active programs from expert configuration
      const response = await authenticatedAxios.get('/expert-programs/active');
      
      if (response.data.success) {
        let types = response.data.programs || [];
        
        // If expert hasn't set up programs yet (needsSetup=true or empty), fall back to all IRCC types
        if (types.length === 0 || response.data.needsSetup) {
          console.log('[CaseFile] Expert has not configured programs, falling back to all IRCC types');
          const fallbackResponse = await authenticatedAxios.get('/ircc/application-types');
          if (fallbackResponse.data.success) {
            types = fallbackResponse.data.applicationTypes || [];
          }
        }
        
        setApplicationTypes(types);
        
        // Load saved program selection and restore it
        const savedProgramId = await loadSelectedProgram();
        if (savedProgramId && types.length > 0) {
          const savedProgram = types.find(t => t.typeId === savedProgramId);
          if (savedProgram) {
            setSelectedProgram(savedProgram);
            // Also load the checklist for the saved program
            loadProgramChecklist(savedProgramId);
          }
        }
      }
    } catch (error) {
      console.log('Error loading application types:', error);
      // Fall back to all IRCC types on error
      try {
        const fallbackResponse = await authenticatedAxios.get('/ircc/application-types');
        if (fallbackResponse.data.success) {
          setApplicationTypes(fallbackResponse.data.applicationTypes || []);
        }
      } catch (fallbackError) {
        console.log('Fallback also failed:', fallbackError);
      }
    }
  }, [authenticatedAxios, canUsePrograms]); // eslint-disable-line react-hooks/exhaustive-deps

  // Initial load
  useEffect(() => {
    loadProgress();
    loadAnalysis();
    loadFiles();
    loadDocuments();
    loadUserFormSessions();
  }, [threadId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Programs load separately: the capability profile arrives after the first render, so
  // this must re-run when it resolves rather than ride on the mount effect.
  useEffect(() => {
    if (canUsePrograms) loadApplicationTypes();
  }, [threadId, canUsePrograms]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    analysisData,
    files,
    documents,
    loading,
    applicationTypes,
    selectedProgram, setSelectedProgram,
    programChecklist, setProgramChecklist,
    userFormSessions,
    loadingProgram,
    pipelineName, setPipelineName,
    loadProgress,
    loadAnalysis,
    loadFiles,
    loadDocuments,
    loadSelectedProgram,
    loadApplicationTypes,
    loadUserFormSessions,
    loadProgramChecklist,
    linkRequirement,
    unlinkRequirement,
    verifyRequirement,
    sendDocumentRequest,
  };
};

export default useCaseData;
